// The close: "Sorted — write it up". What caused it, which reason, one
// optional line for Quality, then Close. One sheet, one button, and the only
// way an andon is done. Lifted from the Andon app's `CloseCard`, drawn as the
// frame draws it (v2.12 template L636-651, script L935).
//
// WHAT THIS IS FOR.
//
// 58.3% of three thousand historical andons closed with nothing recorded on
// them, 6.1% never closed at all, and the dashboard that existed measured
// ticket-hours — off by 731× from how long the line was actually down. Then
// the close became four acts by three people: the owner decided, the operator
// accepted or rejected, silence accepted after a day, and somebody wrote up
// the cause. Measured over seven weeks the verdict was given by silence 57%
// of the time and the write-up was owed on 37% of what got that far.
//
// So it is one act, by the person who sorted it, and it does not happen
// without the cause. TWO TAPS, THEN IT IS DONE: the class, then the reason
// under it. The one line for Quality is optional — the frame's own words,
// "One line for Quality · optional" — because a sentence somebody is made to
// type says "fixed" nine times out of ten, and the chips say more.
//
// WHAT IS DELIBERATELY ABSENT FROM THE FLOW. No verdict. No S1–S4 band. No
// preventive-action box. No minute count to type. No "what kind of problem"
// lens — the tile the operator tapped narrows the cause list on its own and
// is never offered for correction. No pods block — the flowline walk still
// rides with the close, unseen. And no follow-up tasks. The stop and restart
// stamps, which the Andon app put at the top, sit behind one collapsed More
// row under the optional line: they matter on a stopped line, and they are
// never required.
//
// THE OFFER THAT FOLLOWS. A Quality defect andon carries a quality alert;
// closing the andon says the line is running, which says nothing about
// whether the defect is dealt with. So the sheet asks, once, after the close
// has landed — to the people the server says may, and Odoo's approver rule
// still decides at the call.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import type { AlertDetailVM, CauseClass } from '@/api/andon/types';
import { ApiError } from '@/api/http';
import { MentionList, useMentions } from '@/components/andon/MentionPicker';
import { Icon } from '@/components/ui/Icon';
import { Nudge } from '@/components/ui/Nudge';
import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';
import type { Translations } from '@/i18n/strings';
import { closeOwes, closeText, minutesBetween, offerCloseQuality, shortlistCauses } from '@/lib/closeout';
import { starLabel } from '@/lib/severity';
import { clockTime } from '@/lib/thread';
import { dayOf, fromLocalStamp, localStamp } from '@/lib/time';
import { useAndonStations, useCauses } from '@/queries/andon';
import { useStore } from '@/store';

/** Five minutes. What a nudge moves, and what a person recognises as "about". */
const NUDGE_MS = 5 * 60_000;

export function CloseSheet({
  alert,
  open,
  onClose,
  onDone,
  qaName,
}: {
  alert: AlertDetailVM;
  open: boolean;
  /** Put away without closing the andon. */
  onClose: () => void;
  /** The close has landed, and any offer after it is answered. */
  onDone: () => void;
  /** The linked quality alert's own name, for the offer. Null while unknown. */
  qaName: string | null;
}) {
  const t = useT();
  const qc = useQueryClient();
  const showError = useStore((s) => s.showError);
  const showToast = useStore((s) => s.showToast);
  const cheer = useStore((s) => s.cheer);
  const connMode = useStore((s) => s.connMode);
  const isLeader = useStore((s) => s.isLeader);
  const d = alert.closeout;

  /**
   * ONE LINE FOR QUALITY — optional, and it goes in as the closer's message.
   *
   * From the closer's side of the thread, badged, so the raiser reads it the
   * way they read everything else. Mention anybody who needs to know; half of
   * all closes have a consequence for somebody who is not on the andon.
   */
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const box = useRef<HTMLTextAreaElement>(null);
  const mentions = useMentions(text, caret, setText);
  /**
   * Put the caret back where picking a mention left it. A LAYOUT effect, not
   * a frame later — see `Composer`: after a pick the text grows from `@pri`
   * to `@Priya Kaur `, and a `setSelectionRange` from a rAF clamps to the
   * value React has not committed yet.
   */
  useLayoutEffect(() => {
    const at = mentions.pendingCaret.current;
    if (at == null || !box.current) return;
    mentions.pendingCaret.current = null;
    box.current.focus();
    box.current.setSelectionRange(at, at);
    setCaret(at);
  }, [text, mentions]);

  const [offer, setOffer] = useState<'none' | 'asking'>('none');

  const { data: causes } = useCauses(open);
  const classes = causes?.classes ?? [];

  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<number | null>(null);
  const [stoppedAt, setStoppedAt] = useState<number | null>(d.stoppedAt);
  const [restartedAt, setRestartedAt] = useState<number | null>(d.restartedAt);
  const [causePicker, setCausePicker] = useState(false);
  const [causedAt, setCausedAt] = useState(d.causedAt);
  const [allCauses, setAllCauses] = useState(false);
  /** The More row: the stop times and where it was caused, folded away. */
  const [more, setMore] = useState(false);

  /**
   * IT STOPPED THE LINE — the one fact behind More that can go either way.
   *
   * OFFERED ON EVERY ANDON THAT HAS A STOP TO TALK ABOUT, and it starts in the
   * position the record is already in. ON, FROM OFF, IS AN UPGRADE: three
   * stars means the line is down, so the two facts are one fact, and Odoo
   * refuses a stop without it. OFF, FROM ON, IS NOT A DOWNGRADE: what the
   * write-up corrects there is the DOWNTIME, which is what the 731× figure is
   * made of. Three stars is who was called and what deadline they were held
   * to, an hour ago, and rewriting it would measure a three-star response
   * against a two-star SLA.
   */
  const measuredStop = d.stoppedAt != null || d.restartedAt != null;
  const canDeclareStop = measuredStop || d.stopProposal != null;
  const [declaredStop, setDeclaredStop] = useState(measuredStop);
  const upgrades = alert.stars < 3;

  /**
   * Match the prefilled category to a row once the list lands.
   *
   * The andon carries the cause as a KEY, not an id — keys are what the
   * importer and the app both switch on, and ids differ between databases.
   */
  useEffect(() => {
    if (categoryId != null || classes.length === 0) return;
    const match = d.category ? classes.find((c) => c.key === d.category!.key) : null;
    if (match) {
      setCategoryId(match.id);
      const reason = d.subcategory ? match.reasons.find((r) => r.key === d.subcategory!.key) : null;
      if (reason) setSubcategoryId(reason.id);
    }
  }, [classes, categoryId, d.category, d.subcategory]);

  const chosen = useMemo<CauseClass | null>(() => classes.find((c) => c.id === categoryId) ?? null, [classes, categoryId]);
  const reason = useMemo(() => chosen?.reasons.find((r) => r.id === subcategoryId) ?? null, [chosen, subcategoryId]);
  // The tile the operator tapped is the lens, read off the andon. Never a
  // control: the frame has none.
  const shortlist = useMemo(() => shortlistCauses(causes, alert.type, allCauses, categoryId), [causes, alert.type, allCauses, categoryId]);
  const hidden = classes.length - shortlist.length;

  // The moment a timestamp is touched, the delay stops being a measurement.
  // Read off the SWITCH rather than off the stamps, because the switch is what
  // decides whether this andon carries a stop at all — and switching off and
  // back on keeps the stamps' own edits.
  const sentStoppedAt = declaredStop ? stoppedAt : null;
  const sentRestartedAt = declaredStop ? restartedAt : null;
  const moved = sentStoppedAt !== d.stoppedAt || sentRestartedAt !== d.restartedAt;
  const basis = moved ? 'stated' : d.basis;

  /**
   * Now, read ONCE per render so the two stamps agree about which day is today.
   * The device's clock rather than the server-corrected one, deliberately:
   * this is what the platform's own picker will draw.
   */
  const now = Date.now();
  const dayWords = useMemo(() => ({ today: t.dayTodayT, yesterday: t.dayYesterdayT, tomorrow: t.dayTomorrowT }), [t]);

  /**
   * RUNNING BEFORE STOPPED — reachable with a day picker, and worth stopping
   * at the sheet. Refused rather than quietly reordered: which of the two is
   * wrong is something only the person who was there knows.
   */
  const backwards = declaredStop && stoppedAt != null && restartedAt != null && restartedAt < stoppedAt;

  const owes = closeOwes({ categoryId, chosen, subcategoryId, backwards });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['andons'] });

  const save = useMutation({
    mutationFn: () =>
      andonApi.close({
        alertId: alert.id,
        text: closeText(text, chosen, reason),
        categoryId: categoryId!,
        subcategoryId,
        causeWorkcentreId: causedAt?.id ?? null,
        // Null both where the switch is off, so an andon that says the line
        // did not stop cannot also carry a stop window.
        stoppedAt: sentStoppedAt,
        restartedAt: sentRestartedAt,
        // The flowline walk, unseen: the pods the stop held up still ride
        // with the close, the frame simply does not draw them.
        productionIds: d.pods.map((p) => p.id),
        // Three stars means the line is down, so the upgrade and the stop are
        // one fact. Only ever UPWARD, and only where there is something to
        // upgrade from.
        ...(declaredStop && upgrades ? { stars: 3 as const } : {}),
        mentions: mentions.logins(text),
      }),
    onSuccess: () => {
      showToast(t.toastClosedT);
      // The paper. This is the fix landing, and the one moment on this sheet
      // worth marking.
      cheer();
      invalidate();
      if (offerCloseQuality(alert, { mode: connMode === 'user' ? 'user' : connMode === 'kiosk' ? 'kiosk' : 'none', leader: isLeader })) setOffer('asking');
      else onDone();
    },
    onError: (err) => showError(err instanceof ApiError || err instanceof Error ? err.message : t.closeWouldNotSaveT),
  });

  const closeQuality = useMutation({
    mutationFn: () => andonApi.closeQualityAlert(alert.id),
    onSuccess: () => {
      showToast(t.toastQaClosedTooT);
      invalidate();
      // The shop floor's own quality lists, which the sheet behind this reads.
      void qc.invalidateQueries({ queryKey: ['alerts'] });
      onDone();
    },
    // Odoo's own sentence — "not an approver for the team" — and then out.
    // The andon is closed either way; the quality alert stays with Quality.
    onError: (err) => {
      showError(err instanceof ApiError || err instanceof Error ? err.message : t.closeQaFailedT);
      onDone();
    },
  });

  const qa = qaName ?? `QA${alert.qualityAlertId ?? ''}`;

  return (
    <Sheet open={open} onClose={offer === 'asking' ? onDone : onClose} variant="tall" title={offer === 'asking' ? t.closeClosedT : t.closeTitleT} hideTitle>
      {offer === 'asking' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Head kicker={`${alert.qa} · ${alert.workcentre}`} title={t.closeClosedT} />
          <p style={{ margin: 0, font: '500 13px/1.6 Manrope,sans-serif', color: 'var(--ink)' }}>
            {t.closeOfferLeadT} {qa} — {t.closeOfferTailT}
          </p>
          <p style={{ margin: 0, font: '500 11px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>{t.closeOfferHintT}</p>
          <div style={{ display: 'flex', gap: 9 }}>
            <button
              type="button"
              className="press"
              disabled={closeQuality.isPending}
              onClick={() => closeQuality.mutate()}
              style={{ flex: 1, minHeight: 48, borderRadius: 14, border: 'none', background: 'var(--accent)', color: 'var(--accentInk)', font: "700 13.5px 'Space Grotesk',sans-serif", opacity: closeQuality.isPending ? 0.6 : 1 }}
            >
              {closeQuality.isPending ? t.closingT : `${t.closeT} ${qa} ${t.tooT}`}
            </button>
            <button
              type="button"
              className="press"
              disabled={closeQuality.isPending}
              onClick={onDone}
              style={{ flex: 1, minHeight: 48, borderRadius: 14, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', font: '600 13px Manrope,sans-serif' }}
            >
              {t.closeLeaveWithQualityT}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Head kicker={`${alert.qa} · ${alert.workcentre}`} title={t.closeTitleT} tag={t.closeClosesOnSaveT} />

          {/* ── 1 · what caused it ────────────────────────────────────── */}
          <Block title={t.closeWhatCausedItT}>
            <Chips
              options={shortlist.map((c) => ({ id: c.id, label: c.label }))}
              value={categoryId}
              onPick={(id) => {
                setCategoryId(id);
                // A reason belongs to one class. Keeping it across a change would
                // record "ordered late" under a machine breakdown.
                setSubcategoryId(null);
              }}
            />
            {/* THE WHOLE TAXONOMY IS ALWAYS ONE TAP AWAY. */}
            {!allCauses && hidden > 0 && (
              <button
                type="button"
                onClick={() => setAllCauses(true)}
                style={{ marginTop: 8, padding: '7px 11px', borderRadius: 10, border: '1px dashed var(--line)', background: 'none', color: 'var(--sub)', font: '600 11px Manrope,sans-serif', cursor: 'pointer' }}
              >
                {hidden} {t.closeMoreCausesT}
              </button>
            )}
          </Block>

          {/* ── 2 · which reason, where the class has them ────────────── */}
          {chosen && chosen.reasons.length > 0 && (
            <Block title={chosen.key === 'other' ? t.closeAnythingCloserT : t.closeWhichReasonT.replace('{class}', chosen.label.toLowerCase())}>
              <Chips options={chosen.reasons.map((r) => ({ id: r.id, label: r.label }))} value={subcategoryId} onPick={setSubcategoryId} quiet />
            </Block>
          )}

          {/* ── 3 · one line for Quality · optional ───────────────────── */}
          <Block title={t.closeOneLineForQualityT}>
            <textarea
              ref={box}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setCaret(e.target.selectionStart ?? e.target.value.length);
              }}
              onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
              rows={2}
              placeholder={t.closeWhatFixedItPhT}
              aria-label={t.closeOneLineForQualityT}
              style={{ width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: 13, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)', font: '500 13px/1.5 Manrope,sans-serif', resize: 'none', outline: 'none' }}
            />
            <MentionList suggestions={mentions.suggestions} onPick={mentions.pick} above={false} label={t.mentionLabelCloseT} />
          </Block>

          <p style={{ margin: 0, font: '500 11px/1.5 Manrope,sans-serif', color: 'var(--sub)', borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            {alert.qualityAlertId != null ? `${qa} ${t.closeNoteLinkedT}` : t.closeNoteT}
          </p>

          {/* ── more: the stop times and where it was caused, folded away ── */}
          <div style={{ borderRadius: 14, border: '1px solid var(--line)', background: 'var(--surface)', overflow: 'hidden' }}>
            <button
              type="button"
              className="press-row"
              aria-expanded={more}
              onClick={() => setMore((v) => !v)}
              style={{ width: '100%', minHeight: 44, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', color: 'var(--ink)', font: '600 12.5px Manrope,sans-serif' }}
            >
              <span style={{ flex: 1 }}>{t.closeMoreRowT}</span>
              <span style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>{t.closeNeverRequiredT}</span>
              <Icon name="caret" size={14} strokeWidth={2} style={{ color: 'var(--sub)', transform: more ? 'rotate(180deg)' : undefined, transition: 'transform .15s' }} />
            </button>
            {more && (
              <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Block title={t.closeWhatItCostT} hint={!declaredStop ? null : moved ? t.closeNudgedStatedT : `${NUDGE_MS / 60_000} ${t.closeMinATapT}`}>
                  {canDeclareStop && (
                    <StopSwitch
                      on={declaredStop}
                      stars={alert.stars}
                      upgrades={upgrades}
                      onToggle={(next) => {
                        setDeclaredStop(next);
                        // The proposal is a SEED, not a reset: it fills the stamps the
                        // first time the switch goes on with nothing in them, and after
                        // that whatever is on screen is what the person put there.
                        if (!next || stoppedAt != null || restartedAt != null) return;
                        setStoppedAt(d.stopProposal?.stoppedAt ?? null);
                        setRestartedAt(d.stopProposal?.restartedAt ?? null);
                      }}
                    />
                  )}

                  {/* Nothing is being recorded as stopping, so nothing is drawn: two
                      dashes and a zero would read as a measurement of nothing. The
                      sentence is only for the andon with NO switch above it. */}
                  {!declaredStop ? (
                    canDeclareStop ? null : (
                      <p style={{ margin: 0, font: '500 11.5px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>
                        {t.closeNothingStoppedT} {starLabel(alert.stars)}.
                      </p>
                    )
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Stamp label={t.closeStoppedT} at={stoppedAt} basis={basis} now={now} dayWords={dayWords} t={t} onNudge={(by) => setStoppedAt((v) => (v == null ? v : v + by))} onPick={setStoppedAt} />
                        <Stamp label={t.closeRunningT} at={restartedAt} basis={basis} now={now} dayWords={dayWords} t={t} onNudge={(by) => setRestartedAt((v) => (v == null ? v : v + by))} onPick={setRestartedAt} />
                      </div>
                      {/* Said where the two times are, not down at the button. */}
                      {backwards && <p style={{ margin: '8px 2px 0', font: '600 11px/1.5 Manrope,sans-serif', color: 'var(--warn)' }}>{t.closeRunningBeforeStoppedT}</p>}
                      <div style={{ marginTop: 10, padding: '11px 13px', borderRadius: 13, background: 'var(--surface2)', display: 'flex', alignItems: 'baseline', gap: 9 }}>
                        <span className="numeric" style={{ font: "800 26px 'Space Grotesk',sans-serif", color: 'var(--ink)' }}>
                          {minutesBetween(stoppedAt, restartedAt, moved, d.minutes) ?? '—'}
                        </span>
                        <span style={{ font: '600 11px Manrope,sans-serif', color: 'var(--sub)' }}>{t.closeWorkingMinutesT}</span>
                        <span style={{ marginLeft: 'auto', font: '500 10px Manrope,sans-serif', color: 'var(--sub)', textAlign: 'right' }}>
                          {/* WHICH calendar, so the number is checkable rather than
                              merely authoritative. Once the stamps have moved this
                              figure is a subtraction until the save brings the real
                              one back. */}
                          {moved ? t.closeWallClockT : `Odoo · ${d.calendar ?? t.workcentre} ${t.closeOdooCalendarT}`}
                          <br />
                          <b style={{ color: moved ? 'var(--warn)' : 'var(--sub)' }}>{basisWord(basis, t)}</b>
                        </span>
                      </div>
                    </>
                  )}
                </Block>

                <Block title={t.closeCausedAtT}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    {!d.causeConfirmed && causedAt && (
                      <span style={{ padding: '2px 6px', borderRadius: 5, background: 'var(--surface2)', color: 'var(--sub)', font: "700 8.5px 'Space Grotesk',sans-serif", letterSpacing: '.08em', flex: 'none', textTransform: 'uppercase' }}>
                        {t.closeProposedT}
                      </span>
                    )}
                    <span style={{ flex: 1, minWidth: 0, font: '600 12.5px Manrope,sans-serif', color: causedAt ? 'var(--ink)' : 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {causedAt?.name ?? t.closeNotAStationT}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCausePicker(true)}
                      style={{ flex: 'none', padding: '6px 11px', borderRadius: 9, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', font: '600 11px Manrope,sans-serif', cursor: 'pointer' }}
                    >
                      {t.change}
                    </button>
                  </div>
                  <p style={{ margin: '7px 2px 0', font: '500 10.5px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>{t.closeCausedAtHintT}</p>
                </Block>
              </div>
            )}
          </div>

          {/* ── 4 · close <ref> ───────────────────────────────────────── */}
          <button
            type="button"
            className="press"
            disabled={save.isPending || owes != null}
            onClick={() => save.mutate()}
            style={{
              minHeight: 52,
              borderRadius: 15,
              border: owes ? '1px solid var(--line)' : 'none',
              background: owes ? 'var(--surface2)' : 'var(--accent)',
              color: owes ? 'var(--sub)' : 'var(--accentInk)',
              font: "700 14px 'Space Grotesk',sans-serif",
              letterSpacing: '.02em',
              cursor: owes ? 'default' : 'pointer',
              padding: '0 14px',
            }}
          >
            {/* Says what is left rather than scolding, the same way the raise's
                send does. A disabled button with no reason on it is the version
                people tap three times and then give up on. */}
            {save.isPending ? t.closingT : owes === 'order' ? t.closeFixTimesT : owes ? t.closePickCauseReasonT : `${t.closeT} ${alert.qa}`}
          </button>

          <WorkcentrePicker
            open={causePicker}
            onClose={() => setCausePicker(false)}
            onPick={(wc) => {
              setCausedAt(wc);
              setCausePicker(false);
            }}
          />
        </div>
      )}
    </Sheet>
  );
}

/** The sheet's own header: kicker, title, and the tag at the right (frame template L522-525). */
function Head({ kicker, title, tag }: { kicker: string; title: string; tag?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="selectable" style={{ display: 'block', font: "600 12px 'Space Grotesk',sans-serif", color: 'var(--sub)' }}>
          {kicker}
        </span>
        <span style={{ display: 'block', font: "700 19px 'Space Grotesk',sans-serif", marginTop: 2, lineHeight: 1.25, color: 'var(--ink)' }}>{title}</span>
      </span>
      {tag && (
        <span style={{ borderRadius: 7, padding: '3px 9px', font: '700 10.5px Manrope,sans-serif', background: 'rgba(55,199,126,.14)', color: 'var(--pos)', flex: 'none', marginTop: 4, whiteSpace: 'nowrap' }}>
          {tag}
        </span>
      )}
    </div>
  );
}

function basisWord(basis: string, t: Translations): string {
  switch (basis) {
    case 'measured':
      return t.closeBasisMeasuredT;
    case 'stated':
      return t.closeBasisStatedT;
    case 'none':
      return t.closeBasisNoneT;
    default:
      return t.closeBasisUnknownT;
  }
}

/**
 * "It stopped the line" — starting wherever the record already is.
 *
 * A SWITCH AND NOT A CHIP, because the two states are never equal. One of them
 * is what the andon has been claiming since it was raised; the other is a
 * person overruling it. THE EMPHASIS FOLLOWS THE ASSERTION, NOT THE POSITION:
 * on a one- or two-star the on position is the overruling one, so on is
 * tinted; on a three-star it is the other way round. IT SAYS WHAT IT WILL DO,
 * in both directions, rather than springing it on save.
 */
function StopSwitch({ on, stars, upgrades, onToggle }: { on: boolean; stars: number; upgrades: boolean; onToggle: (next: boolean) => void }) {
  const t = useT();
  const asserting = upgrades ? on : !on;
  const tone = upgrades ? 'var(--danger)' : 'var(--warn)';
  const tint = upgrades ? 'rgba(255,90,95,.12)' : 'rgba(240,161,58,.12)';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onToggle(!on)}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', marginBottom: 10, borderRadius: 13, background: asserting ? tint : 'var(--surface)', border: `1px solid ${asserting ? tone : 'var(--line)'}`, textAlign: 'left', cursor: 'pointer' }}
    >
      <span aria-hidden="true" style={{ width: 38, height: 22, flex: 'none', borderRadius: 11, background: on ? (upgrades ? 'var(--danger)' : 'var(--accent)') : 'var(--line)', position: 'relative', transition: 'background .15s ease' }}>
        <span style={{ position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .15s ease' }} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', font: '700 12.5px Manrope,sans-serif', color: 'var(--ink)' }}>{t.closeItStoppedLineT}</span>
        <span style={{ display: 'block', font: '500 10.5px/1.5 Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
          {upgrades
            ? on
              ? t.closeSwitchOnUpgradeT
              : `${t.closeSwitchOffUpgradeT} (${starLabel(stars as 1 | 2 | 3)})`
            : on
              ? t.closeSwitchOnMeasuredT
              : t.closeSwitchOffMeasuredT}
        </span>
      </span>
    </button>
  );
}

/**
 * One timestamp: nudged for a correction, picked for a different day.
 *
 * TWO CONTROLS, BECAUSE THERE ARE TWO JOBS. The carets move five minutes a
 * tap, and that is what almost every write-up needs. But the stepper alone
 * quietly assumed the stop was TODAY: a night-shift stop written up the next
 * morning is twelve hours of nudging. The picker is the platform's own,
 * opened by tapping the time, and it moves days as easily as minutes.
 *
 * AND THE DAY IS ALWAYS ON SCREEN. `06:20` is a complete reading of a stop
 * inside one shift and an unanswerable one the moment the write-up outlives
 * the day.
 */
function Stamp({
  label,
  at,
  basis,
  now,
  dayWords,
  t,
  onNudge,
  onPick,
}: {
  label: string;
  at: number | null;
  basis: string;
  now: number;
  dayWords: { today: string; yesterday: string; tomorrow: string };
  t: Translations;
  onNudge: (by: number) => void;
  onPick: (at: number) => void;
}) {
  const day = at == null ? null : dayOf(at, now, dayWords);
  const otherDay = day != null && day !== dayWords.today;
  return (
    <div style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 13, background: 'var(--surface)', border: '1px solid var(--line)' }}>
      <div style={{ font: '600 10px Manrope,sans-serif', color: 'var(--sub)' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
        <Nudge label={`${label} ${t.closeFiveEarlierT}`} onClick={() => onNudge(-NUDGE_MS)} dir="down" disabled={at == null} />
        {/* THE NATIVE CONTROL, behind the time rather than beside it. The
            platform's picker is the one a thumb already knows, and it handles
            a locale, a month boundary and a screen reader without being asked. */}
        <label
          onClick={(e) => {
            // Focus alone opens the picker on a phone. A desktop browser wants
            // asking — guarded because the call throws where the control is a
            // set of typed segments and there is no picker to show.
            const input = e.currentTarget.querySelector('input');
            if (input?.showPicker == null) return;
            try {
              input.showPicker();
            } catch {
              /* No picker to open — the field itself is still editable. */
            }
          }}
          style={{ flex: 1, minWidth: 0, position: 'relative', display: 'block', padding: '2px 0 3px', borderRadius: 9, background: 'var(--surface2)', textAlign: 'center', cursor: at == null ? 'default' : 'pointer' }}
        >
          <span className="numeric" style={{ display: 'block', font: "700 16px 'Space Grotesk',sans-serif", color: 'var(--ink)' }}>
            {at == null ? '—' : clockTime(at)}
          </span>
          <span style={{ display: 'block', marginTop: 1, font: `${otherDay ? 700 : 500} 9.5px Manrope,sans-serif`, color: otherDay ? 'var(--ink)' : 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {/* A hard space rather than nothing, so a stamp with no time to
                show is the same height as the one beside it. */}
            {day ?? ' '}
          </span>
          {at != null && (
            <input
              type="datetime-local"
              aria-label={`${label} ${t.closeDateAndTimeT}`}
              value={localStamp(at)}
              // No future stop. The picker refusing it is kinder than a save
              // that comes back rejected.
              max={localStamp(now)}
              onChange={(e) => {
                const next = fromLocalStamp(e.target.value);
                if (next != null) onPick(next);
              }}
              style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
            />
          )}
        </label>
        <Nudge label={`${label} ${t.closeFiveLaterT}`} onClick={() => onNudge(NUDGE_MS)} dir="up" disabled={at == null} />
      </div>
      <div style={{ marginTop: 2, font: "700 8.5px 'Space Grotesk',sans-serif", letterSpacing: '.08em', color: basis === 'stated' ? 'var(--warn)' : 'var(--sub)', textTransform: 'uppercase' }}>
        {basisWord(basis, t)}
      </div>
    </div>
  );
}

/** A question on the sheet, in the frame's register: bold, 12.5px, with the chips under it. */
function Block({ title, hint, children }: { title: string; hint?: string | null; children: React.ReactNode }) {
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ font: '700 12.5px Manrope,sans-serif', color: 'var(--ink)' }}>{title}</span>
        {hint && <span style={{ marginLeft: 'auto', font: '500 10px Manrope,sans-serif', color: 'var(--sub)' }}>{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Chips({ options, value, onPick, quiet }: { options: Array<{ id: number; label: string }>; value: number | null; onPick: (id: number) => void; quiet?: boolean }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
      {options.map((option) => {
        const on = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(option.id)}
            style={{ minHeight: 38, padding: quiet ? '0 11px' : '0 13px', borderRadius: 11, border: `1px solid ${on ? 'var(--ink)' : 'transparent'}`, background: on ? 'var(--ink)' : 'var(--surface2)', color: on ? 'var(--bg)' : 'var(--ink)', font: `600 ${quiet ? 11.5 : 12}px Manrope,sans-serif`, cursor: 'pointer' }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** The station list, behind Change. */
function WorkcentrePicker({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (wc: { id: number; name: string } | null) => void }) {
  const t = useT();
  const [term, setTerm] = useState('');
  const { data: stations } = useAndonStations(open);

  const rows = useMemo(() => {
    const q = term.trim().toLowerCase();
    const all = stations?.workcentres ?? [];
    // Capped at forty. Past that the list is not being read, it is being
    // scrolled — and the search box above is the answer to a long list.
    return (q ? all.filter((w) => w.name.toLowerCase().includes(q)) : all).slice(0, 40);
  }, [stations, term]);

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={t.closeWhereCausedT}>
      <input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        data-autofocus
        placeholder={t.closeSearchStationsT}
        aria-label={t.closeSearchStationsT}
        style={{ width: '100%', boxSizing: 'border-box', height: 44, padding: '0 14px', borderRadius: 13, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)', font: '500 13px Manrope,sans-serif', outline: 'none', marginBottom: 10 }}
      />
      {/* NOT A STATION is a first-class answer and sits at the top, not buried
          under forty rows. Roughly a third of causes are Planning,
          Procurement, IT, Engineering or a supplier. */}
      <button
        type="button"
        className="press-row"
        onClick={() => onPick(null)}
        style={{ width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 12, border: '1px dashed var(--line)', background: 'none', color: 'var(--sub)', font: '600 12.5px Manrope,sans-serif', cursor: 'pointer', marginBottom: 8 }}
      >
        {t.closeNotAStationLongT}
      </button>
      {rows.map((wc) => (
        <button
          key={wc.id}
          type="button"
          className="press-row"
          onClick={() => onPick(wc)}
          style={{ width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 12, border: 'none', background: 'none', color: 'var(--ink)', font: '600 13px Manrope,sans-serif', cursor: 'pointer' }}
        >
          {wc.name}
        </button>
      ))}
    </Sheet>
  );
}
